import { FC, JSX, useState, useCallback, useEffect, ChangeEvent } from "react";
import { Text } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { t } from "@lingui/macro";
import Input from "src/atoms/Input";
import { Button } from "src/atoms/Button";
import { isValidEmail as validateEmail } from "src/utils/validations";
import { useResetPassword } from "src/hooks/useResetPassword";
import { formatEmailAsCensored } from "src/utils/formatEmailAsCensored";
import { ApolloError } from "@apollo/client";
import { formErrors } from "src/translations/formErrors";
import { useLingui } from "@lingui/react";

interface Props {
  onSuccessfulSubmit(email: string): void;
  emailAddress?: string;
  customInput?: (hasErrors: boolean, value?: string) => JSX.Element;
  width?: Array<string | number>;
}

const ResetPasswordForm: FC<Props> = ({
  onSuccessfulSubmit,
  customInput,
  width = ["min(100%, 352px)"],
  emailAddress = "",
}) => {
  const { _, i18n } = useLingui();
  const [email, setEmail] = useState<string>(emailAddress);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(
    validateEmail(emailAddress)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { resetPassword, loading } = useResetPassword();

  useEffect(() => {
    setEmail(emailAddress);
  }, [emailAddress]);

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setErrorMessage(null);
      const { value } = e.target;
      setEmail(value);
      setIsValidEmail(validateEmail(value));
    },
    [setEmail, setIsValidEmail]
  );
  const handleResetPassword = useCallback(
    async (e: ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        await resetPassword(email);
        setErrorMessage(null);
        onSuccessfulSubmit(formatEmailAsCensored(email));
      } catch (err) {
        let message = "It looks like something went wrong our side.";
        if (err instanceof ApolloError) {
          message = err.graphQLErrors?.[0]?.message ?? message;
        }
        setErrorMessage(message);
      }
    },
    [email, resetPassword, onSuccessfulSubmit, setErrorMessage]
  );

  const translatedError =
    errorMessage && formErrors[errorMessage]
      ? _(formErrors[errorMessage])
      : errorMessage || "";

  return (
    <Box mt={3} width={width}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleResetPassword}
        noValidate
      >
        {!customInput ? (
          <Input
            data-testid="reset-password-form-email-input"
            my={3}
            type="text"
            placeholder={t(i18n)`Email address`}
            name="email"
            onChange={handleEmailChange}
            required
            isError={!!errorMessage}
          />
        ) : (
          customInput(!!errorMessage, email)
        )}
        {translatedError && (
          <Text
            mt={2}
            mb={3}
            color={"semantic.red"}
            textAlign="left"
            fontSize={0}
            lineHeight="1.43"
          >
            {translatedError}
          </Text>
        )}
        <Button
          type="submit"
          disabled={!isValidEmail || loading}
          fullWidth
          variant="primary"
        >
          {t(i18n)`reset my password`}
        </Button>
      </form>
    </Box>
  );
};

export default ResetPasswordForm;
